import React, { useState, useEffect, useId } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TagsSelector, Tag } from "@/components/ui/tags-selector";
import { useNavigate } from 'react-router-dom';
import { toast } from "@/components/ui/sonner";
import { Loader2, Music, User, Pencil, Plus } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { motion, AnimatePresence } from "framer-motion";
import { RainbowButton } from "@/components/ui/rainbow-button";
import { SlideButton } from "@/components/ui/slide-button";
import LoadingPage from "./LoadingPage";

const ALL_TAG_OPTIONS: Tag[] = [
  { id: "Hip-Hop", label: "Hip-Hop 🎵", interest: "Hip Hop" },
  { id: "NBA", label: "NBA 🏀", interest: "NBA" },
  { id: "cooking", label: "Cooking 🥘", interest: "Cooking" },
  { id: "Constellation Names", label: "Constellation Names 🌠", interest: "Constellation Names" },
  { id: "90s Rom Com Movies", label: "90s Rom Com Movies 🎥", interest: "90s Rom Com Movies" },
  { id: "Cereal Brands", label: "Cereal Brands 🥣", interest: "Cereal Brands" },
  { id: "Dog Breeds", label: "Dog Breeds 🐶", interest: "Dog Breeds" },
  { id: "Indoor Plants", label: "Indoor Plants 🌱", interest: "Indoor Plants" },
  { id: "2000s Pop Music", label: "2000s Pop Music 💿", interest: "2000s Pop Music" },
  { id: "The Office ", label: "The Office 🖨️", interest: "The Office" },
  { id: "Cult Classic Films", label: "Cult Classic Films 📽️", interest: "Cult Classic Films" },
  { id: "Ted Lasso Characters", label: "Ted Lasso Characters ⚽", interest: "Ted Lasso Characters" },
  { id: "Pasta Shapes", label: "Pasta Shapes 🍝", interest: "Pasta Shapes" },
  { id: "Guess the Country", label: "Guess the Country 🌎", interest: "Guess the Country" },
  { id: "Pokémon ", label: "Pokémon 🔥", interest: "Pokémon " },
  { id: "Famous Landmarks", label: "Famous Landmarks 🗼", interest: "Famous Landmarks" },
  { id: "Taylor Swift Songs", label: "Taylor Swift Songs 🎤", interest: "Taylor Swift Songs" },
  { id: "11th Grade Biology", label: "11th Grade Biology 🧬", interest: "11th Grade Biology" },
  { id: "U.S. Presidents", label: "U.S. Presidents 👨‍💼", interest: "U.S. Presidents" },
  { id: "Studio Ghibli Films", label: "Studio Ghibli Films 👻", interest: "Studio Ghibli Films" },
  { id: "Marvel Heroes", label: "Marvel Heroes 🦸", interest: "Marvel Heroes" },
  { id: "World War II", label: "World War II 🪖", interest: "World War II" },
  { id: "Knitting Stitches", label: "Knitting Stitches 🧵", interest: "Knitting Stitches" },
  { id: "Twitter Memes", label: "Twitter Memes 🐦‍⬛", interest: "Twitter Memes" },
  { id: "Avatar: The Last Airbender", label: "Avatar: The Last Airbender 💨", interest: "Avatar: The Last Airbender" },
  { id: "BoJack Horseman", label: "BoJack Horseman 🐴", interest: "BoJack Horseman" },
  { id: "Sports GOATs", label: "Sports GOATs 🐐", interest: "Sports GOATs" },
  { id: "Harry Potter", label: "Harry Potter ⚡ ͛", interest: "Harry Potter" },
  { id: "Greek Gods & Heroes", label: "Greek Gods & Heroes 🔱", interest: "Greek Gods & Heroes" },
  { id: "anime", label: "Anime 💢", interest: "Anime" }
];

// Function to get random tags
const getRandomTags = (count: number): Tag[] => {
  const shuffled = [...ALL_TAG_OPTIONS].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

// Get 10 random tags for display
const TAG_OPTIONS: Tag[] = getRandomTags(11);

const InterestForm: React.FC = () => {
  const [redditUsername, setRedditUsername] = useState("");
  const [manualInterestInput, setManualInterestInput] = useState("");
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [tempTag, setTempTag] = useState<Tag | null>(null);
  const [removingTag, setRemovingTag] = useState<Tag | null>(null);
  const [manualTags, setManualTags] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const manualInputId = useId();
  const [slideReset, setSlideReset] = useState(0);

  useEffect(() => {
    if (loading) {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          // Use a smaller increment and update more frequently
          return prev + (90 / 700); // Increment by 90/200 each 50ms to reach 90% in 10 seconds
        });
      }, 50); // Update every 50ms instead of 1000ms
      return () => clearInterval(interval);
    } else {
      setProgress(0);
    }
  }, [loading]);

  // Handle manual input add:
  const handleAddManualInterest = (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();
    const value = manualInterestInput.trim();
    if (!value) return;
    
    if (selectedTags.length >= 5) {
      toast.error("You can only select up to 5 interests");
      return;
    }
    
    // Add as a tag unless duplicate.
    if (!selectedTags.some(tag => tag.id.toLowerCase() ===  value.toLowerCase().replace(/\s+/g, "-"))) {
      const newTag = {
        id: value.toLowerCase().replace(/\s+/g, "-"),
        label: value,
        interest: value
      };
      // Set temporary tag for animation
      setTempTag(newTag);
      // Add to selected tags after a short delay to allow animation
      setTimeout(() => {
        setSelectedTags(prev => [...prev, newTag]);
        setManualTags(prev => new Set([...prev, newTag.id]));
        setTempTag(null);
      }, 100);
    }
    else {
      toast.error("You already added this interest");
    }
    setManualInterestInput("");
  };

  const handleEnterKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission
      handleAddManualInterest();
    }
  };

  const handleRemoveTag = (tag: Tag) => {
    if (manualTags.has(tag.id)) {
      setRemovingTag(tag);
      setTimeout(() => {
        setSelectedTags(prev => prev.filter(t => t.id !== tag.id));
        setManualTags(prev => {
          const newSet = new Set(prev);
          newSet.delete(tag.id);
          return newSet;
        });
        setRemovingTag(null);
      }, 200);
    } else {
      setSelectedTags(prev => prev.filter(t => t.id !== tag.id));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTags.length === 0) {
      toast.error("Please enter at least one interest");
      return;
    }
    setLoading(true);
    // Use the interest field instead of stripping emojis from the label
    const interestList = selectedTags.map(tag => tag.interest);
    try {
      let generateCrossword;
      if(import.meta.env.MODE === 'development') {
        generateCrossword = httpsCallable<{ interests: string[] }, any>(functions, 'generateCrossword');
      }
      else {
        generateCrossword = httpsCallable<{ interests: string[] }, any>(functions, 'generateCrossword',{ limitedUseAppCheckTokens: true });
      }
      const result = await generateCrossword({ interests: interestList });
      if (result.data && result.data.data) {
        setProgress(100);
        setTimeout(() => {
          navigate('/crossword', {
            state: {
              grid: result.data.data.grid,
              acrossClues: result.data.data.acrossClues,
              downClues: result.data.data.downClues,
              title: result.data.data.title,
              uuid: result.data.data.uuid
            }
          });
        }, 500);
      } else {
        throw new Error('No data received from server');
      }
    } catch (error) {
      console.error('Error generating crossword:', error);
      toast.error('Failed to generate crossword. Please try again.');
      setLoading(false);
    }
  };

  // Handler for SlideButton
  const handleSlideComplete = async () => {
    if (selectedTags.length === 0) {
      toast.error("Please enter at least one interest");
      setSlideReset((r) => r + 1); // Reset the slide button
      return;
    }
    setLoading(true);
    // Use the interest field instead of stripping emojis from the label
    const interestList = selectedTags.map(tag => tag.interest);
    try {
      let generateCrossword;
      if(import.meta.env.MODE === 'development') {
        generateCrossword = httpsCallable<{ interests: string[] }, any>(functions, 'generateCrossword');
      }
      else {
        generateCrossword = httpsCallable<{ interests: string[] }, any>(functions, 'generateCrossword',{ limitedUseAppCheckTokens: true });
      }
      const result = await generateCrossword({ interests: interestList });
      if (result.data && result.data.data) {
        setProgress(100);
        setTimeout(() => {
          navigate('/crossword', {
            state: {
              grid: result.data.data.grid,
              acrossClues: result.data.data.acrossClues,
              downClues: result.data.data.downClues,
              title: result.data.data.title,
              uuid: result.data.data.uuid
            }
          });
        }, 500);
      } else {
        throw new Error('No data received from server');
      }
    } catch (error) {
      console.error('Error generating crossword:', error);
      toast.error('Failed to generate crossword. Please try again.');
      setLoading(false);
      setSlideReset((r) => r + 1); // Reset the slide button on error
    }
  };

  if (loading) {
    return <LoadingPage progress={progress} />;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-xl mx-auto flex flex-col items-center gap-8"
    >
      <Accordion
        type="single"
        defaultValue="manual"
        collapsible
        className="w-full rounded-xl shadow-lg bg-card/80 ring-1 ring-border overflow-hidden"
      >
        {/* Manual */}
        <AccordionItem
          value="manual"
          className="bg-gradient-to-r from-[#AEC8A4]/60 via-[#AEC8A4]/30 to-[#AEC8A4]/40"
        >
          <AccordionTrigger className="py-4 px-6 text-lg leading-6 flex items-center gap-2">
            <Pencil size={22} className="text-primary" />
            <span className="font-medium">Pick Your Passions</span>
          </AccordionTrigger>
          <AccordionContent className="pb-4 px-6">
            {/* Inline input with end button */}
            <div className="text-center text-green-900 mb-4">
              Add up to 5 topics to shape your puzzle. The more niche, the better!
            </div>
            <div className="relative mb-2 max-w-md mx-auto">
              <Input
                id={manualInputId}
                placeholder='e.g. "2000s Tv Shows", "K-Pop", "Exoctic Pets"'
                value={manualInterestInput}
                onChange={e => setManualInterestInput(e.target.value.slice(0, 50))}
                onKeyDown={handleEnterKey}
                className="pe-9 bg-white/70 backdrop-blur-[2.5px] h-12 text-base text-green-900"
                autoComplete="off"
                type="text"
                maxLength={50}
              />
              <button
                type="button"
                onClick={(e) => handleAddManualInterest(e)}
                className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-lg border border-transparent text-muted-foreground/80 outline-offset-2 transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Add Interest"
              >
                <Plus size={16} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
            {/* Temporary tag for animation */}
            <div className="relative h-0">
              <AnimatePresence>
                {tempTag && (
                  <motion.div
                    layoutId={`tag-${tempTag.id}`}
                    className="absolute -top-2 right-0 flex items-center gap-1 px-4 py-2.5 bg-gray-100/60 rounded-full shrink-0 shadow pointer-events-none"
                    style={{
                      borderRadius: 14,
                    }}
                    initial={{ opacity: 0, y: 20, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <motion.span
                      layoutId={`tag-${tempTag.id}-label`}
                      className="text-gray-700 font-medium"
                    >
                      {tempTag.label}
                    </motion.span>
                  </motion.div>
                )}
                {removingTag && (
                  <motion.div
                    layoutId={`tag-${removingTag.id}`}
                    className="absolute -top-2 left-0 flex items-center gap-1 px-4 py-2.5 bg-gray-100/60 rounded-full shrink-0 shadow pointer-events-none"
                    style={{
                      borderRadius: 14,
                    }}
                    initial={{ opacity: 1, y: 0, scale: 1 }}
                    animate={{ opacity: 0, y: 20, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <motion.span
                      layoutId={`tag-${removingTag.id}-label`}
                      className="text-gray-700 font-medium"
                    >
                      {removingTag.label}
                    </motion.span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {/* TagsSelector */}
            <TagsSelector
              tags={TAG_OPTIONS}
              selectedTags={selectedTags}
              onChange={(newTags) => {
                if (newTags.length > 5) {
                  toast.error("You can only select up to 5 interests");
                  return;
                }
                setSelectedTags(newTags);
              }}
              onRemoveTag={handleRemoveTag}
              className="mt-2"
            />
          </AccordionContent>
        </AccordionItem>

        {/* Spotify */}
        <AccordionItem
          value="spotify"
          className="bg-gradient-to-r from-[#E7EFC7]/80 via-[#E7EFC7]/50 to-[#E7EFC7]/60 border-b border-border opacity-80"
        >
          <AccordionTrigger className="py-4 px-6 text-lg leading-6">
            <div className="flex items-center w-full">
              <Music size={22} className="text-green-900" />
              <div className="flex-1 flex items-center justify-center gap-2 text-center">
                <span className="font-medium">Link Your Spotify</span>
                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Coming Soon</span>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 px-6 flex flex-col items-stretch">
            <div className="text-center text-muted-foreground">
              This feature will be available in a future update.
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Reddit */}
        <AccordionItem
          value="reddit"
          className="bg-gradient-to-r from-[#CBA35C]/40 via-[#CBA35C]/20 to-[#CBA35C]/30 border-b border-border opacity-80"
        >
          <AccordionTrigger className="py-4 px-6 text-lg leading-6">
            <div className="flex items-center w-full">
              <User size={22} className="text-orange-900" />
              <div className="flex-1 flex items-center justify-center gap-2 text-center">
                <span className="font-medium">Sync Your Reddit Account</span>
                <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">Coming Soon</span>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 px-6">
            <div className="text-center text-muted-foreground">
              This feature will be available in a future update.
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <div className="w-full max-w-xs mt-2">
        <SlideButton
          className="w-full py-3 rounded-xl shadow text-base font-bold tracking-wide text-white bg-gradient-to-r from-purple-500 to-purple-700"
          loading={loading}
          onSlideComplete={handleSlideComplete}
          resetTrigger={slideReset}
        >
          Build My Crossword
        </SlideButton>
      </div>
    </form>
  );
};

export default InterestForm;
